/**
 * ResidentTrendEpisodes — the campus-wide Resident Trends & Actions widget
 * (episode model). One card per resident, ranked by concern score; the feed
 * shows the top 15 by default with a "show all" overflow (flag budget,
 * plan section 6.2). Used by both the V2 dashboard and the legacy dashboard.
 *
 * Props: campusId
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getUserId, getSessionId } from '../../services/session.js';
import { fetchResidentEpisodes, updateResidentEpisode, updateCommunityInsight } from './episodesApi.js';
import EpisodeCard from './EpisodeCard.jsx';
import { ACTION_OPTIONS, TRIGGER_CONFIG } from './trendConfig.js';

const TOP_N = 15;

const actionLabel = (value) => {
    const opt = ACTION_OPTIONS.find((o) => o.value === value);
    return opt ? opt.label.toLowerCase() : 'staff action';
};

const winName = (win) => {
    const live = `${win.resident_first_name || ''} ${win.resident_last_name || ''}`.trim();
    return live || win.resident_name_snapshot || 'A resident';
};

const ResidentTrendEpisodes = ({ campusId }) => {
    const [episodes, setEpisodes] = useState([]);
    const [wins, setWins] = useState([]);
    const [insights, setInsights] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAll, setShowAll] = useState(false);
    const [processingIds, setProcessingIds] = useState(new Set());
    const [actionError, setActionError] = useState(null);

    const fetchData = useCallback(async () => {
        setLoading(true);
        setActionError(null);
        const result = await fetchResidentEpisodes({ campusId, limit: 200 });
        setEpisodes(result.episodes || []);
        setWins(result.wins || []);
        setInsights(result.insights || []);
        setLoading(false);
    }, [campusId]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const applyUpdate = useCallback(async (episodeId, status, extra = {}) => {
        setProcessingIds((prev) => new Set(prev).add(episodeId));
        setActionError(null);
        const [ok, errMsg] = await updateResidentEpisode({
            campusId,
            episodeId,
            status,
            ...extra,
            userId: getUserId(),
            clientId: getSessionId(),
        });
        if (ok) {
            setEpisodes((prev) => (
                status === 'acknowledged'
                    ? prev.map((e) => (e.episode_id === episodeId ? { ...e, status: 'acknowledged' } : e))
                    : prev.filter((e) => e.episode_id !== episodeId)
            ));
        } else {
            setActionError(errMsg || 'Update failed. Please try again.');
        }
        setProcessingIds((prev) => { const n = new Set(prev); n.delete(episodeId); return n; });
    }, [campusId]);

    const handleAcknowledge = useCallback((id) => applyUpdate(id, 'acknowledged'), [applyUpdate]);
    const handleResolve = useCallback((id, actionTaken) => applyUpdate(id, 'resolved', { actionTaken }), [applyUpdate]);
    const handleDismiss = useCallback((id, reason, muteDays) => applyUpdate(id, 'dismissed', { dismissReason: reason, muteDays }), [applyUpdate]);

    const handleInsight = useCallback(async (insightId, status) => {
        setActionError(null);
        const [ok, errMsg] = await updateCommunityInsight({
            campusId, insightId, status, userId: getUserId(), clientId: getSessionId(),
        });
        if (ok) {
            setInsights((prev) => (
                status === 'acknowledged'
                    ? prev.map((i) => (i.insight_id === insightId ? { ...i, status: 'acknowledged' } : i))
                    : prev.filter((i) => i.insight_id !== insightId)
            ));
        } else {
            setActionError(errMsg || 'Update failed. Please try again.');
        }
    }, [campusId]);

    const visible = useMemo(() => (showAll ? episodes : episodes.slice(0, TOP_N)), [episodes, showAll]);
    const highCount = episodes.filter((e) => (e.score || 0) >= 70).length;

    return (
        <div className="md-section" style={{ overflow: 'visible', padding: 0 }}>
            {/* Header */}
            <div style={{ background: '#1a8a7d', padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderRadius: 'var(--radius-lg) var(--radius-lg) 0 0' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ color: '#fff', fontSize: 'var(--font-size-base)', fontWeight: 600 }}>Resident Trends & Actions</span>
                    {episodes.length > 0 ? (
                        <span style={{ fontSize: 'var(--font-size-xs)', fontWeight: 600, padding: '2px 10px', borderRadius: 10, background: highCount > 0 ? 'rgba(231,76,60,0.25)' : 'rgba(255,255,255,0.2)', color: '#fff' }}>
                            {episodes.length} resident{episodes.length !== 1 ? 's' : ''}{highCount > 0 ? ` · ${highCount} high priority` : ''}
                        </span>
                    ) : null}
                </div>
                <button onClick={fetchData} disabled={loading} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 14px', background: 'transparent', border: '1px solid rgba(255,255,255,0.3)', color: '#fff' }}>
                    {loading ? 'Loading...' : 'Refresh'}
                </button>
            </div>

            {/* Content */}
            <div style={{ padding: '12px 20px 16px' }}>
                {insights.map((insight) => (
                    <div key={insight.insight_id} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, margin: '0 0 10px', padding: '10px 14px', borderRadius: 'var(--radius-md)', background: 'rgba(230,126,34,0.08)', border: '1px solid rgba(230,126,34,0.25)' }}>
                        <span style={{ fontSize: 16 }}>{TRIGGER_CONFIG[insight.trigger_type]?.icon || '📢'}</span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 600, color: 'var(--color-text-heading)' }}>{insight.title}</span>
                                {insight.status === 'acknowledged' ? (
                                    <span style={{ fontSize: 10, fontWeight: 600, color: '#e67e22', background: 'rgba(230,126,34,0.12)', padding: '1px 8px', borderRadius: 'var(--radius-full)', textTransform: 'uppercase' }}>Acknowledged</span>
                                ) : null}
                            </div>
                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginTop: 2, lineHeight: 1.5 }}>{insight.description}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                            {insight.status === 'new' ? (
                                <button onClick={() => handleInsight(insight.insight_id, 'acknowledged')} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px' }}>Acknowledge</button>
                            ) : null}
                            <button onClick={() => handleInsight(insight.insight_id, 'closed')} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 12px', color: 'var(--color-text-muted)' }}>Close</button>
                        </div>
                    </div>
                ))}
                {wins.length > 0 ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', margin: '0 0 10px' }}>
                        <span style={{ fontSize: 10, fontWeight: 600, color: '#16a085', textTransform: 'uppercase', letterSpacing: 0.4 }}>Recent wins</span>
                        {wins.map((win) => (
                            <span key={win.episode_id} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 'var(--font-size-xs)', padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'rgba(22,160,133,0.10)', color: '#16a085' }} title={`Resolved ${String(win.closed_date || '').slice(0, 10)}; call pattern returned toward baseline`}>
                                ✓ {winName(win)}: improved after {actionLabel(win.action_taken)}
                            </span>
                        ))}
                    </div>
                ) : null}
                {actionError ? (
                    <div style={{ margin: '0 0 8px', padding: '6px 12px', borderRadius: 'var(--radius-md)', background: 'rgba(231,76,60,0.08)', color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)' }}>
                        {actionError}
                    </div>
                ) : null}
                {loading && episodes.length === 0 ? (
                    <div style={{ padding: 'var(--space-8) 0', textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)' }}>Loading resident trends...</div>
                ) : episodes.length === 0 ? (
                    <div style={{ padding: 'var(--space-8) 0', textAlign: 'center' }}>
                        <div style={{ fontSize: 28, marginBottom: 'var(--space-2)' }}>✓</div>
                        <div style={{ color: 'var(--color-text-heading)', fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>No open concerns</div>
                        <div style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-xs)', marginTop: 4 }}>All resident trends are within normal ranges</div>
                    </div>
                ) : (
                    <>
                        {visible.map((episode) => (
                            <EpisodeCard
                                key={episode.episode_id}
                                episode={episode}
                                onAcknowledge={handleAcknowledge}
                                onResolve={handleResolve}
                                onDismiss={handleDismiss}
                                isProcessing={processingIds.has(episode.episode_id)}
                            />
                        ))}
                        {episodes.length > TOP_N ? (
                            <div style={{ textAlign: 'center', marginTop: 'var(--space-2)' }}>
                                <button onClick={() => setShowAll(!showAll)} className="rp-btn rp-btn--secondary" style={{ fontSize: 'var(--font-size-xs)', padding: '4px 14px' }}>
                                    {showAll ? `Show top ${TOP_N}` : `Show all ${episodes.length}`}
                                </button>
                            </div>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};

export default ResidentTrendEpisodes;
